import { useState, useEffect, useRef } from "react";
import { Form, Button, Spinner } from "react-bootstrap";
import * as Yup from "yup";
import { Formik } from "formik";
import { APIContact } from "../helper/APIFunctions.js";
import "./ContactPage.css";
import "./NewHomePage.css";
import "./OrbitPilotSignup.css";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Required"),
  isPrincipalInvestigator: Yup.string().required("Required"),
  studyTitle: Yup.string().required("Required"),
  institution: Yup.string().required("Required"),
  conditions: Yup.object().test(
    'at-least-one',
    'Please select at least one condition',
    (value) => {
      if (!value) return false;
      return Object.values(value).some(v => v === true);
    }
  ),
  conditionOther: Yup.string().when('conditions', {
    is: (conditions) => conditions?.other === true,
    then: (schema) => schema.required("Please specify the condition"),
    otherwise: (schema) => schema.notRequired(),
  }),
  statementMeanings: Yup.object().test(
    'at-least-one',
    'Please check at least one option',
    (value) => {
      if (!value) return false;
      return Object.values(value).some(v => v === true);
    }
  ),
  acknowledgement: Yup.boolean().oneOf([true], "You must acknowledge this statement to proceed"),
  email: Yup.string().required("Required").email("Invalid email address"),
});

const OrbitPilotSignup = () => {
  const canvasRef = useRef(null);
  const [status, setStatus] = useState({});

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let stars = [];
    const numStars = 250;
    let animationFrameId;

    const handleResize = () => {
      const w = window.innerWidth;
      const h = document.body.scrollHeight;
      canvas.width = w;
      canvas.height = h;
      stars = [];
      for (let i = 0; i < numStars; i++) {
        stars.push({
          x: Math.random() * w,
          y: Math.random() * h,
          size: Math.random() * 1.5 + 0.5,
          speed: Math.random() * 0.4 + 0.1,
        });
      }
    };

    function draw() {
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "rgba(255, 255, 255, 0.8)";

      stars.forEach(star => {
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fill();
        star.y -= star.speed;
        if (star.y < 0) {
          star.y = canvas.height;
          star.x = Math.random() * canvas.width;
        }
      });

      animationFrameId = requestAnimationFrame(draw);
    }

    handleResize();
    draw();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return (
    <div
      className="homepage-container orbit-pilot-page"
      style={{
        width: '100%',
        position: 'relative',
        overflowX: 'hidden',
        background: 'linear-gradient(to bottom, #0a0f2c, #1b1f3a, #2d304d)',
      }}
    >
      <canvas
        id="stars"
        ref={canvasRef}
        style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}
      ></canvas>
      <div
        className="overlay"
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'radial-gradient(circle at center, rgba(0,0,0,0.1), rgba(0,0,0,0.98))',
          pointerEvents: 'none',
          zIndex: 1
        }}
      ></div>

      <div className="main-container-narrower orbit-pilot-form" style={{ position: 'relative', zIndex: 2, background: 'transparent' }}>
        <h1 className="orbit-pilot-title">Orbit Pilot Program</h1>
        <p className="orbit-pilot-subtitle">We’re offering a no-cost pilot to a small number of actively recruiting, IRB-approved studies. 
        </p>

        {status.success ? (
          <div className="success-message">
            <h3>Thank you for your interest!</h3>
            <p>We've received your application and will be in touch within 2 business days to discuss next steps.</p>
          </div>
        ) : (
          <>
            {/* What the pilot includes */}
            <div className="pilot-info-section">
              <h2 className="section-title">What the pilot includes</h2>
              
              <div className="text-content">
                <h3>Private study portal</h3>
                <ul>
                  <li>A secure, study-specific mobile/web portal for participants and caregivers</li>
                  <li>Access controlled by the study team (no public distribution)</li>
                </ul>

                <h3>Automated visual explainers</h3>
                <ul>
                  <li>Narrated visual explainers, generated based on study-approved text (protocol excerpts, consent language, study updates)</li>
                  <li>Automatic adaptation based on comprehension level, language, and interaction patterns.</li>
                </ul>

                <h3>Research update translation</h3>
                <ul>
                  <li>New study publications or abstracts converted into simple, scrollable explanations for participants and caregivers</li>
                  <li>Designed to support understanding, not promotion</li>
                </ul>

                <h3>Voice-based science explainer</h3>
                <ul>
                  <li>Optional voice interface allowing participants or caregivers to ask questions about study concepts using approved content only</li>
                </ul>

                <h3>PI control</h3>
                <ul>
                  <li>All content sourced from or approved by the study team</li>
                  <li>No generative content outside provided materials</li>
                </ul>
              </div>
            </div>

            {/* What the pilot does not include */}
            <div className="pilot-info-section">
              <h2 className="section-title">What the pilot does not include</h2>
              <ul className="exclusion-list">
                <li>No recruitment or advertising tools</li>
                <li>No medical advice or treatment guidance</li>
                <li>No data collection, outcome measurement, or endpoint analysis</li>
                <li>No changes to existing EDC, ePRO, or DHT systems</li>
                <li>No patient data resale or secondary use</li>
                <li>No obligation to purchase or continue after the pilot</li>
              </ul>
            </div>

            {/* Application Form */}
            <div className="pilot-form-section">
              <h2 className="section-title">Study Interest Information</h2>
              {status.error ? <p className="text-danger">{status.error}</p> : null}
              
              <Formik
                initialValues={{
                  name: "",
                  isPrincipalInvestigator: "",
                  studyTitle: "",
                  institution: "",
                  conditions: {
                    dementia: false,
                    parkinsons: false,
                    other: false,
                  },
                  conditionOther: "",
                  statementMeanings: {
                    useful: false,
                    learnMore: false,
                    followUp: false,
                  },
                  acknowledgement: false,
                  email: "",
                }}
                validationSchema={validationSchema}
                onSubmit={(values, actions) => {
                  // Format conditions
                  const selectedConditions = [];
                  if (values.conditions.dementia) selectedConditions.push('Dementia / ADRD');
                  if (values.conditions.parkinsons) selectedConditions.push("Parkinson's");
                  if (values.conditions.other) {
                    selectedConditions.push(`Other: ${values.conditionOther}`);
                  }

                  // Format statement meanings
                  const selectedMeanings = [];
                  if (values.statementMeanings.useful) selectedMeanings.push('This type of tool could be useful for my study');
                  if (values.statementMeanings.learnMore) selectedMeanings.push('I would be open to learning more about a pilot deployment');
                  if (values.statementMeanings.followUp) selectedMeanings.push('I would be open to being contacted for a follow-up discussion');

                  // Format the message for the contact API
                  const formattedMessage = `
ORBIT PILOT PROGRAM - STUDY INTEREST STATEMENT

Name: ${values.name}
Role: ${values.isPrincipalInvestigator}

Study Information:
Study title / NCT number: ${values.studyTitle}
Institution: ${values.institution}
Study condition(s): ${selectedConditions.join(', ')}

I have reviewed a high-level description of the Orbit patient-facing explainer platform being developed by Snorkle Labs.

What this statement means (selected):
${selectedMeanings.map(m => '- ' + m).join('\n')}

ACKNOWLEDGEMENT: ${values.acknowledgement ? 'YES' : 'NO'}

Contact Email: ${values.email}
Date: ${new Date().toLocaleDateString()}
                  `;

                  const contactData = {
                    email: values.email,
                    header: "Orbit Pilot Program - Study Interest Statement",
                    comment: formattedMessage,
                  };

                  APIContact(
                    contactData,
                    () => {
                      actions.setSubmitting(false);
                      setStatus({ success: true });
                    },
                    (err) => {
                      actions.setSubmitting(false);
                      setStatus({ error: err });
                    }
                  );
                }}
              >
                {({
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  values,
                  touched,
                  errors,
                  isSubmitting,
                  setFieldValue,
                }) => {
                  // Progressive disclosure logic - check if each section is complete
                  const section1Complete = values.name && values.isPrincipalInvestigator;
                  const section2Complete = section1Complete && values.studyTitle && values.institution;
                  const section3Complete = section2Complete && 
                    (values.conditions.dementia || values.conditions.parkinsons || 
                     (values.conditions.other && values.conditionOther));
                  const section4Complete = section3Complete && 
                    (values.statementMeanings.useful || values.statementMeanings.learnMore || values.statementMeanings.followUp);
                  const section5Complete = section4Complete && values.acknowledgement;
                  const section6Complete = section5Complete && values.email;

                  return (
                    <Form noValidate onSubmit={handleSubmit}>
                      {/* Section 1: Personal Information */}
                      <div className="form-section">
                        <h3 className="form-section-title">Personal Information</h3>
                        
                        <Form.Group className="my-3">
                          <Form.Label>Your Name *</Form.Label>
                          <Form.Control
                            name="name"
                            placeholder="Full name"
                            value={values.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isSubmitting}
                            isInvalid={touched.name && errors.name}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.name}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="my-3">
                          <Form.Label>Role *</Form.Label>
                          <div className="role-checkboxes">
                            <Form.Check
                              type="radio"
                              name="isPrincipalInvestigator"
                              id="role-pi"
                              label="I am the Principal Investigator (or Co-Investigator) of this study"
                              value="Principal Investigator (or Co-Investigator)"
                              checked={values.isPrincipalInvestigator === 'Principal Investigator (or Co-Investigator)'}
                              onChange={handleChange}
                              disabled={isSubmitting}
                            />
                            <Form.Check
                              type="radio"
                              name="isPrincipalInvestigator"
                              id="role-not-pi"
                              label="I am not the Principal Investigator or Co-Investigator"
                              value="Not Principal Investigator"
                              checked={values.isPrincipalInvestigator === 'Not Principal Investigator'}
                              onChange={handleChange}
                              disabled={isSubmitting}
                            />
                          </div>
                          {touched.isPrincipalInvestigator && errors.isPrincipalInvestigator && (
                            <div className="invalid-feedback d-block">{errors.isPrincipalInvestigator}</div>
                          )}
                        </Form.Group>
                      </div>

                      {/* Section 2: Study Information */}
                      <div className={`form-section ${!section1Complete ? 'form-section-disabled' : ''}`}>
                        <h3 className="form-section-title">Study Information</h3>
                        
                        <Form.Group className="my-3">
                          <Form.Label>Study title / NCT number *</Form.Label>
                          <Form.Control
                            name="studyTitle"
                            placeholder="Enter study title or NCT number"
                            value={values.studyTitle}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isSubmitting || !section1Complete}
                            isInvalid={touched.studyTitle && errors.studyTitle}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.studyTitle}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="my-3">
                          <Form.Label>Institution *</Form.Label>
                          <Form.Control
                            name="institution"
                            placeholder="University or research center"
                            value={values.institution}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isSubmitting || !section1Complete}
                            isInvalid={touched.institution && errors.institution}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.institution}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </div>

                      {/* Section 3: Study Conditions */}
                      <div className={`form-section ${!section2Complete ? 'form-section-disabled' : ''}`}>
                        <h3 className="form-section-title">Study condition(s) *</h3>
                        <p className="form-section-subtitle">Check all that apply</p>
                        
                        <div className="condition-checkboxes">
                          <Form.Check
                            type="checkbox"
                            name="conditions.dementia"
                            id="condition-dementia"
                            label="Dementia / ADRD"
                            checked={values.conditions.dementia}
                            onChange={handleChange}
                            disabled={isSubmitting || !section2Complete}
                          />
                          <Form.Check
                            type="checkbox"
                            name="conditions.parkinsons"
                            id="condition-parkinsons"
                            label="Parkinson's"
                            checked={values.conditions.parkinsons}
                            onChange={handleChange}
                            disabled={isSubmitting || !section2Complete}
                          />
                          <div className="condition-other">
                            <Form.Check
                              type="checkbox"
                              name="conditions.other"
                              id="condition-other"
                              label="Other:"
                              checked={values.conditions.other}
                              onChange={handleChange}
                              disabled={isSubmitting || !section2Complete}
                            />
                            {values.conditions.other && (
                              <Form.Control
                                name="conditionOther"
                                placeholder="Please specify the condition"
                                value={values.conditionOther}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isSubmitting || !section2Complete}
                                isInvalid={touched.conditionOther && errors.conditionOther}
                                className="ms-2"
                                style={{ display: 'inline-block', width: 'auto', minWidth: '250px' }}
                              />
                            )}
                          </div>
                        </div>
                        {touched.conditions && errors.conditions && (
                          <div className="invalid-feedback d-block">{errors.conditions}</div>
                        )}
                        {touched.conditionOther && errors.conditionOther && (
                          <div className="invalid-feedback d-block">{errors.conditionOther}</div>
                        )}
                      </div>

                      {/* Section 4: What this statement means */}
                      <div className={`form-section ${!section3Complete ? 'form-section-disabled' : ''}`}>
                        <h3 className="form-section-title">What this statement means (non-binding) *</h3>
                        <p className="form-section-subtitle">By checking at least one box below, I am indicating that:</p>
                        
                        <div className="statement-meanings-checkboxes">
                          <Form.Check
                            type="checkbox"
                            name="statementMeanings.useful"
                            id="meaning-useful"
                            label="This type of tool could be useful for my study"
                            checked={values.statementMeanings.useful}
                            onChange={handleChange}
                            disabled={isSubmitting || !section3Complete}
                          />
                          <Form.Check
                            type="checkbox"
                            name="statementMeanings.learnMore"
                            id="meaning-learn-more"
                            label="I would be open to learning more about a pilot deployment"
                            checked={values.statementMeanings.learnMore}
                            onChange={handleChange}
                            disabled={isSubmitting || !section3Complete}
                          />
                          <Form.Check
                            type="checkbox"
                            name="statementMeanings.followUp"
                            id="meaning-follow-up"
                            label="I would be open to being contacted for a follow-up discussion"
                            checked={values.statementMeanings.followUp}
                            onChange={handleChange}
                            disabled={isSubmitting || !section3Complete}
                          />
                        </div>
                        {touched.statementMeanings && errors.statementMeanings && (
                          <div className="invalid-feedback d-block">{errors.statementMeanings}</div>
                        )}

                        <div className="understanding-section my-4">
                          <h4>What this statement does not mean</h4>
                          <ul>
                            <li>It is not a contract, agreement, or memorandum of understanding</li>
                            <li>It does not commit my study or institution to participate</li>
                            <li>It does not authorize access to patient data</li>
                            <li>It does not involve payment, procurement, or vendor selection</li>
                          </ul>
                        </div>
                      </div>

                      {/* Section 5: Acknowledgement */}
                      <div className={`form-section ${!section4Complete ? 'form-section-disabled' : ''}`}>
                        <Form.Group className="my-4">
                          <Form.Check
                            type="checkbox"
                            name="acknowledgement"
                            id="acknowledgement"
                            label="I acknowledge and understand the above statements *"
                            checked={values.acknowledgement}
                            onChange={handleChange}
                            disabled={isSubmitting || !section4Complete}
                            isInvalid={touched.acknowledgement && errors.acknowledgement}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.acknowledgement}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </div>

                      {/* Section 6: Contact Email */}
                      <div className={`form-section ${!section5Complete ? 'form-section-disabled' : ''}`}>
                        <h3 className="form-section-title">Contact Information</h3>
                        
                        <Form.Group className="my-3">
                          <Form.Label>Email *</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            placeholder="Contact email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isSubmitting || !section5Complete}
                            isInvalid={touched.email && errors.email}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </div>

                      <Button
                        type="submit"
                        className="orbit-submit-button mt-4"
                        disabled={isSubmitting || !section6Complete || status.success}
                      >
                        {isSubmitting ? (
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : (
                          "Submit Statement"
                        )}
                      </Button>
                      <p className="form-disclaimer mt-3">
                        * Required fields. By submitting, you agree to be contacted by Snorkle Labs regarding the Orbit pilot program.
                      </p>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrbitPilotSignup;
